/**
 * Fonte de dados em `localStorage`, para usar o painel sem Firebase provisionado.
 *
 * Ela imita as garantias de produção de propósito, e não só a forma dos dados:
 *
 *  - `publish()` roda `collectPublishBlockers` e **recusa** um draft inválido, como a Function faz;
 *  - o valor de um segredo, uma vez gravado, não volta por nenhum método — só o nome aparece;
 *  - `published` só muda por `publish()`, nunca por `saveDraft()`.
 *
 * Sem essas três, o painel seria construído contra um contrato mais frouxo que o real e passaria a
 * quebrar justamente ao ligar no Firebase.
 */

import {
  collectPublishBlockers,
  createDefaultAppearance,
  createStarterFlow,
  sameVersionContent,
  type WebchatVersion,
} from '@merlin/flow-engine';
import {
  generatePublicKey,
  PublishBlockedError,
  type AnalyticsDay,
  type LeadEntry,
  type VersionEntry,
  type DataSource,
  type DraftPatch,
  type MonthlyUsage,
  type WebchatDoc,
  type WebchatSummary,
} from './types.js';

const DOCS_KEY = 'merlin.demo.webchats';
const SECRETS_KEY = 'merlin.demo.secrets';
const VERSIONS_KEY = 'merlin.demo.versions';
const DEMO_OWNER = 'demo-owner';

type DocMap = Record<string, WebchatDoc>;
type SecretMap = Record<string, Record<string, string>>;

function readDocs(): DocMap {
  try {
    return JSON.parse(localStorage.getItem(DOCS_KEY) ?? '{}') as DocMap;
  } catch {
    return {};
  }
}

function writeDocs(docs: DocMap): void {
  localStorage.setItem(DOCS_KEY, JSON.stringify(docs));
}

function readSecrets(): SecretMap {
  try {
    return JSON.parse(localStorage.getItem(SECRETS_KEY) ?? '{}') as SecretMap;
  } catch {
    return {};
  }
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/** Histórico por webchat: `{ [webchatId]: { [version]: {...} } }`. */
type VersionMap = Record<string, Record<string, WebchatVersion & { version: number; publishedAt: number; nodeCount: number }>>;

function readVersions(): VersionMap {
  try {
    return JSON.parse(localStorage.getItem(VERSIONS_KEY) ?? '{}') as VersionMap;
  } catch {
    return {};
  }
}

function writeVersions(mapa: VersionMap): void {
  localStorage.setItem(VERSIONS_KEY, JSON.stringify(mapa));
}

export function createDemoSource(): DataSource {
  return {
    kind: 'demo',

    async list(): Promise<WebchatSummary[]> {
      return Object.values(readDocs())
        .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
        .map((doc) => ({
          id: doc.id,
          name: doc.name,
          publicKey: doc.publicKey,
          publishedVersion: doc.published?.version ?? null,
          updatedAt: doc.updatedAt,
        }));
    },

    async get(id: string): Promise<WebchatDoc | null> {
      const doc = readDocs()[id];
      return doc ? clone(doc) : null;
    },

    async create(name: string): Promise<string> {
      const docs = readDocs();
      const id = `demo-${Date.now().toString(36)}`;
      docs[id] = {
        id,
        ownerId: DEMO_OWNER,
        name: name.trim() || 'Webchat sem nome',
        publicKey: generatePublicKey(),
        // `localhost` já entra para o testbed funcionar de imediato; `127.0.0.1` fica de fora
        // porque é o par que demonstra o bloqueio por domínio.
        authorizedDomains: ['localhost'],
        outboundAllowlist: [],
        secretNames: [],
        draft: { appearance: createDefaultAppearance(), flow: createStarterFlow() },
        published: null,
        updatedAt: Date.now(),
      };
      writeDocs(docs);
      return id;
    },

    async saveDraft(id: string, patch: DraftPatch): Promise<void> {
      const docs = readDocs();
      const doc = docs[id];
      if (!doc) throw new Error('Webchat não encontrado.');

      docs[id] = {
        ...doc,
        ...(patch.name !== undefined ? { name: patch.name } : {}),
        ...(patch.authorizedDomains !== undefined ? { authorizedDomains: patch.authorizedDomains } : {}),
        ...(patch.outboundAllowlist !== undefined ? { outboundAllowlist: patch.outboundAllowlist } : {}),
        ...(patch.collectLeads !== undefined ? { collectLeads: patch.collectLeads } : {}),
        ...(patch.conversationLimit !== undefined ? { conversationLimit: patch.conversationLimit } : {}),
        ...(patch.requestLimit !== undefined ? { requestLimit: patch.requestLimit } : {}),
        draft: {
          appearance: patch.appearance ?? doc.draft.appearance,
          flow: patch.flow ?? doc.draft.flow,
        },
        // `published` não é tocado aqui — é o ponto todo do versionamento.
        published: doc.published,
        updatedAt: Date.now(),
      };
      writeDocs(docs);
    },

    async publish(id: string): Promise<{ version: number; unchanged: boolean }> {
      const docs = readDocs();
      const doc = docs[id];
      if (!doc) throw new Error('Webchat não encontrado.');

      const blockers = collectPublishBlockers(doc.draft, doc.authorizedDomains);
      if (blockers.length) {
        throw new PublishBlockedError('Corrija antes de publicar.', blockers);
      }

      // Conteúdo idêntico não vira versão nova, igual à Function. Sem esta linha aqui, o painel em
      // modo demo teria um comportamento que produção não tem.
      if (doc.published && sameVersionContent(doc.published, doc.draft)) {
        return { version: doc.published.version, unchanged: true };
      }

      const version = (doc.published?.version ?? 0) + 1;
      docs[id] = {
        ...doc,
        published: { ...clone(doc.draft), version },
        updatedAt: Date.now(),
      };
      writeDocs(docs);

      // Registra no histórico, como a Function faz — a fonte demo imita as garantias de produção,
      // senão a interface é construída contra um contrato que produção não oferece.
      const versoes = readVersions();
      versoes[id] = {
        ...(versoes[id] ?? {}),
        [String(version)]: {
          ...clone(doc.draft),
          version,
          publishedAt: Date.now(),
          nodeCount: doc.draft.flow.nodes.length,
        },
      };
      writeVersions(versoes);

      return { version, unchanged: false };
    },

    async remove(id: string): Promise<void> {
      const docs = readDocs();
      delete docs[id];
      writeDocs(docs);
      const secrets = readSecrets();
      delete secrets[id];
      localStorage.setItem(SECRETS_KEY, JSON.stringify(secrets));
      const versoes = readVersions();
      delete versoes[id];
      writeVersions(versoes);
    },

    async upsertSecret(id: string, name: string, value: string): Promise<void> {
      const docs = readDocs();
      const doc = docs[id];
      if (!doc) throw new Error('Webchat não encontrado.');

      const secrets = readSecrets();
      secrets[id] = { ...(secrets[id] ?? {}), [name]: value };
      localStorage.setItem(SECRETS_KEY, JSON.stringify(secrets));

      if (!doc.secretNames.includes(name)) {
        docs[id] = { ...doc, secretNames: [...doc.secretNames, name], updatedAt: Date.now() };
        writeDocs(docs);
      }
    },

    async deleteSecret(id: string, name: string): Promise<void> {
      const docs = readDocs();
      const doc = docs[id];
      if (!doc) return;

      const secrets = readSecrets();
      if (secrets[id]) {
        delete secrets[id][name];
        localStorage.setItem(SECRETS_KEY, JSON.stringify(secrets));
      }
      docs[id] = {
        ...doc,
        secretNames: doc.secretNames.filter((n) => n !== name),
        updatedAt: Date.now(),
      };
      writeDocs(docs);
    },

    /*
     * Analytics e leads vêm do widget rodando num site de verdade, e o preview do painel não envia
     * nada (métrica de teste não pode virar número de produção). No modo demo, portanto, estas duas
     * listas são legitimamente vazias — a interface mostra o estado vazio em vez de dado inventado.
     */
    async listLeads(): Promise<LeadEntry[]> {
      return [];
    },

    async deleteLead(): Promise<void> {
      /* nada a apagar no modo demo */
    },

    async getAnalytics(): Promise<AnalyticsDay[]> {
      return [];
    },

    /*
     * A fonte demo não recebe eventos do widget, então o consumo é sempre zero. Devolver zero em vez
     * de lançar mantém a paridade que o resto do arquivo segue: a interface é construída contra o
     * mesmo contrato que produção oferece.
     */
    async getMonthlyUsage(): Promise<MonthlyUsage> {
      return { conversations: 0, requests: 0 };
    },

    async listVersions(id: string, max = 20): Promise<VersionEntry[]> {
      return Object.values(readVersions()[id] ?? {})
        // Ordenação numérica: comparar as chaves como texto colocaria a v10 antes da v2.
        .sort((a, b) => b.version - a.version)
        .slice(0, max)
        .map((v) => ({ version: v.version, publishedAt: v.publishedAt, nodeCount: v.nodeCount }));
    },

    async getVersion(id: string, version: number): Promise<WebchatVersion | null> {
      const registro = readVersions()[id]?.[String(version)];
      return registro ? { appearance: clone(registro.appearance), flow: clone(registro.flow) } : null;
    },

    async uploadButtonImage(): Promise<string> {
      /*
       * Não há Storage no modo demo. Um `data:` URL resolveria a exibição, mas o schema aceita só
       * http(s) em `appearance.imageUrl` — e por bom motivo: a imagem embutida iria dentro da
       * config pública, baixada por todo visitante. Melhor falhar dizendo o caminho certo do que
       * deixar o usuário descobrir o problema na hora de publicar.
       */
      throw new Error(
        'Upload de imagem exige o Firebase Storage e não funciona no modo demo. ' +
          'Cole uma URL https da imagem no campo ao lado.',
      );
    },
  };
}
